import { CommonUtils } from "../../util/common-utils";
import {
    JOB_SCHEDULE_TYPE_VALUES,
    JobsDashboardScheduleType,
    JobsDashboardStatus,
    JobsDashboardUtils,
    type JobsDashboardRecord,
    type JobsDashboardValue,
} from "./jobs-dashboard-utils";
import type { JobsDashboardForm } from "./jobs-dashboard-form";

type JobRecord = JobsDashboardRecord;
type JobHistoryEntry = JobsDashboardRecord;

export class JobsDashboardRender {
    private readonly form: JobsDashboardForm;

    private readonly roles = JobsDashboardUtils.ROLES;

    public constructor(form: JobsDashboardForm) {
        this.form = form;
    }

    public setStatus(text: string): void {
        const el = this.form.getStatusEl();
        if (el instanceof HTMLElement) {
            el.textContent = text;
        }
    }

    public setLatestResult(text: string): void {
        const el = this.form.getLatestResultEl();
        if (el instanceof HTMLElement) {
            el.textContent = text;
        }
    }

    public renderList(
        jobs: JobRecord[],
        _historyByJob: Map<string, JobHistoryEntry[]>,
        editingJobKeys: ReadonlySet<string> = new Set(),
    ): void {
        const listEl = this.form.getListEl();
        if (!(listEl instanceof HTMLElement)) {
            return;
        }

        if (!Array.isArray(jobs) || jobs.length === 0) {
            listEl.innerHTML = '<div class="jobs-dashboard__empty">No jobs found.</div>';
            return;
        }

        const html = jobs
            .map((job, index) => {
                const key = JobsDashboardUtils.normalizeJobKey(job.job_key);
                const isEditing = editingJobKeys.has(key);
                const name = CommonUtils.escapeHtml(JobsDashboardUtils.toText(job.job_name || job.command || "-"));
                const descriptionsRaw = JobsDashboardUtils.toText(job.descriptions || "-");
                const descriptionsTooltip = CommonUtils.escapeHtmlAttribute(descriptionsRaw);
                const jobGroup = CommonUtils.escapeHtml(JobsDashboardUtils.toText(job.job_group || "-"));
                const scheduleTypeEnum = JobsDashboardUtils.normalizeScheduleType(job.schedule_type);
                const scheduleType = CommonUtils.escapeHtml(scheduleTypeEnum);
                const scheduleBadgeClass = this.getScheduleBadgeClass(scheduleTypeEnum);
                const scheduleTimeText = JobsDashboardUtils.toText(job.schedule_time || "");
                const scheduleTime = CommonUtils.escapeHtml(scheduleTimeText || "-");
                const scheduleTypeValue = CommonUtils.escapeHtmlAttribute(scheduleTypeEnum);
                const scheduleTimeValue = CommonUtils.escapeHtmlAttribute(scheduleTimeText);
                const scheduleDayValue = CommonUtils.escapeHtmlAttribute(
                    JobsDashboardUtils.toText(job.schedule_day ?? ""),
                );
                const startedAt = CommonUtils.escapeHtml(this.formatDateTimeForCell(job.started_at));
                const endedAt = CommonUtils.escapeHtml(this.formatDateTimeForCell(job.ended_at));
                const active = JobsDashboardUtils.toBool(job.is_active);
                const activeClass = active
                    ? "jobs-dashboard__state-badge--active"
                    : "jobs-dashboard__state-badge--inactive";
                const activeText = active ? "active" : "inactive";
                const jobStatusEnum = JobsDashboardUtils.normalizeStatus(job.job_status);
                const isRunning = jobStatusEnum === JobsDashboardStatus.Running;
                const statusLabel = CommonUtils.escapeHtml(jobStatusEnum);
                const statusBadgeClass = this.getStatusBadgeClass(jobStatusEnum);
                const keyAttr = CommonUtils.escapeHtmlAttribute(key);
                const runDisabled = active && !isRunning ? "" : "disabled";
                const runLabel = isRunning ? "Running..." : "Run";
                const actionIconRole = isRunning ? this.roles.ABORT_BUTTON : this.roles.EDIT_BUTTON;
                const actionCircleType = isRunning ? " abort" : " edit";
                const actionIconClass = isRunning ? " jobs-dashboard__icon-btn--abort" : "";
                const editActiveClass = !isRunning && isEditing ? " jobs-dashboard__icon-btn--active" : "";
                const actionIconTitle = isRunning ? "Abort" : "Edit";
                const actionIconLabel = isRunning ? "Abort" : "Edit";
                const scheduleDetail = CommonUtils.escapeHtml(
                    this.formatScheduleDetail(scheduleTypeEnum, scheduleDayValue),
                );

                return `
                    <div class="jobs-dashboard__row" data-job-key="${keyAttr}" data-editing="${isEditing ? "1" : "0"}">
                        <div class="jobs-dashboard__cell jobs-dashboard__cell--no">
                            <span class="jobs-dashboard__no-value">${index + 1}</span>
                        </div>
                        <div class="jobs-dashboard__cell jobs-dashboard__cell--identity">
                            <div class="jobs-dashboard__identity">
                                <span class="jobs-dashboard__identity-group">${jobGroup}</span>
                                <span class="jobs-dashboard__identity-name" title="${descriptionsTooltip}">${name}</span>
                            </div>
                        </div>
                        <div class="jobs-dashboard__cell jobs-dashboard__cell--schedule">
                            <div class="jobs-dashboard__stack jobs-dashboard__stack--editor">
                                <div class="jobs-dashboard__display-value jobs-dashboard__stack jobs-dashboard__stack--compact">
                                    <span class="badge badge--mini jobs-dashboard__schedule-badge ${scheduleBadgeClass}">${scheduleType}</span>
                                    ${scheduleDetail !== "" ? `<span class="jobs-dashboard__stack-value jobs-dashboard__schedule-detail">${scheduleDetail}</span>` : ""}
                                </div>
                                <div class="jobs-dashboard__schedule-editor jobs-dashboard__editor-panel">
                                    <select class="textbox jobs-dashboard__input" data-field="schedule_type">
                                        ${JOB_SCHEDULE_TYPE_VALUES.map((value) => `<option value="${CommonUtils.escapeHtmlAttribute(value)}" ${scheduleTypeValue === value ? "selected" : ""}>${CommonUtils.escapeHtml(value)}</option>`).join("")}
                                    </select>
                                    <input class="textbox jobs-dashboard__input jobs-dashboard__input--day" data-field="schedule_day" type="number" min="1" max="31" placeholder="day" value="${scheduleDayValue}">
                                </div>
                            </div>
                        </div>
                        <div class="jobs-dashboard__cell jobs-dashboard__cell--time">
                            <div class="jobs-dashboard__stack jobs-dashboard__stack--editor">
                                <div class="jobs-dashboard__display-value jobs-dashboard__stack jobs-dashboard__stack--compact">
                                    <span class="jobs-dashboard__stack-value jobs-dashboard__stack-value--time">${scheduleTime}</span>
                                </div>
                                <input class="textbox jobs-dashboard__input jobs-dashboard__editor-panel" data-field="schedule_time" type="text" inputmode="numeric" placeholder="HH:MM" value="${scheduleTimeValue}">
                            </div>
                        </div>
                        <div class="jobs-dashboard__cell jobs-dashboard__cell--active">
                            <div class="jobs-dashboard__stack jobs-dashboard__stack--editor">
                                <div class="jobs-dashboard__display-value jobs-dashboard__stack jobs-dashboard__stack--compact">
                                    <span class="badge badge--mini jobs-dashboard__state-badge ${activeClass}">${activeText}</span>
                                </div>
                                <select class="textbox jobs-dashboard__input jobs-dashboard__editor-panel" data-field="is_active">
                                    <option value="1" ${active ? "selected" : ""}>active</option>
                                    <option value="0" ${!active ? "selected" : ""}>inactive</option>
                                </select>
                            </div>
                        </div>
                        <div class="jobs-dashboard__cell jobs-dashboard__cell--status">
                            <div class="jobs-dashboard__stack jobs-dashboard__stack--compact">
                                <span class="badge badge--mini jobs-dashboard__status-badge ${statusBadgeClass}">${statusLabel}</span>
                            </div>
                        </div>
                        <div class="jobs-dashboard__cell jobs-dashboard__cell--started-ended">
                            <div class="jobs-dashboard__stack jobs-dashboard__stack--time">
                                <span class="jobs-dashboard__stack-line">
                                    <span class="jobs-dashboard__time-label">Started:</span>
                                    <span class="jobs-dashboard__time-value">${startedAt}</span>
                                </span>
                                <span class="jobs-dashboard__stack-line">
                                    <span class="jobs-dashboard__time-label">Ended:</span>
                                    <span class="jobs-dashboard__time-value">${endedAt}</span>
                                </span>
                            </div>
                        </div>
                        <div class="jobs-dashboard__cell jobs-dashboard__cell--action">
                            <div class="jobs-dashboard__action-buttons">
                                <button type="button" class="btn-circle${actionCircleType} jobs-dashboard__icon-btn${actionIconClass}${editActiveClass}" data-role="${actionIconRole}" data-job-key="${keyAttr}" title="${actionIconTitle}" aria-label="${actionIconLabel}"></button>
                                ${isEditing ? `<button type="button" class="btn save jobs-dashboard__save-btn" data-role="${this.roles.SAVE_BUTTON}" data-job-key="${keyAttr}">Save</button>` : `<button type="button" class="btn run jobs-dashboard__run-btn" data-role="${this.roles.RUN_BUTTON}" data-job-key="${keyAttr}" ${runDisabled}>${runLabel}</button>`}
                            </div>
                        </div>
                    </div>
                `;
            })
            .join("");

        listEl.innerHTML = html;
    }

    public formatDateTimeForCell(value: JobsDashboardValue): string {
        const raw = JobsDashboardUtils.toText(value).trim();
        if (raw === "") {
            return "-";
        }

        const parsed = new Date(raw);
        if (Number.isNaN(parsed.getTime())) {
            return raw;
        }

        const yyyy = parsed.getFullYear();
        const mm = String(parsed.getMonth() + 1).padStart(2, "0");
        const dd = String(parsed.getDate()).padStart(2, "0");
        const hh = String(parsed.getHours()).padStart(2, "0");
        const mi = String(parsed.getMinutes()).padStart(2, "0");
        const ss = String(parsed.getSeconds()).padStart(2, "0");

        return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
    }

    public getScheduleBadgeClass(scheduleType: JobsDashboardScheduleType): string {
        if (scheduleType === JobsDashboardScheduleType.Hourly) {
            return "jobs-dashboard__schedule-badge--hourly";
        }

        if (scheduleType === JobsDashboardScheduleType.Daily) {
            return "jobs-dashboard__schedule-badge--daily";
        }

        if (scheduleType === JobsDashboardScheduleType.Monthly) {
            return "jobs-dashboard__schedule-badge--monthly";
        }

        return "jobs-dashboard__schedule-badge--default";
    }

    public getStatusBadgeClass(status: JobsDashboardStatus): string {
        if (status === JobsDashboardStatus.Running) {
            return "jobs-dashboard__status-badge--running";
        }

        if (status === JobsDashboardStatus.Finished) {
            return "jobs-dashboard__status-badge--finished";
        }

        if (status === JobsDashboardStatus.Failed) {
            return "jobs-dashboard__status-badge--failed";
        }

        return "jobs-dashboard__status-badge--pending";
    }

    public formatScheduleDetail(scheduleType: JobsDashboardScheduleType, scheduleDay: string): string {
        if (scheduleType === JobsDashboardScheduleType.Hourly) {
            return "every hour";
        }

        if (scheduleType === JobsDashboardScheduleType.Monthly) {
            const day = scheduleDay.trim();
            return day !== "" ? `day ${day}` : "monthly";
        }

        return "";
    }
}
